<script lang="ts" setup>
import ReviewCard from '~/components/cards/ReviewCard.vue'
import AiReviews from '~/components/products/AiReviews.vue'
import { useWindowHelpers } from '~/composables/window-helpers'
import helpers from '~/helpers'
import type { AiTag } from '~/types/ai-reviews'
import type { Page } from '~/types/json-api'
import type { ReviewData } from '~/types/reviews'

const { $apiHelper, $toast } = useNuxtApp()
const { scrollTo } = useWindowHelpers()

const messages = {
  failedToDeleteReview: 'Не удалось удалить отзыв.',
  failedToGetReviews: 'Не удалось получить отзывы.',
  noReviews: 'Нет отзывов о товаре.',
  reviewIsDeleted: 'Отзыв удалён.'
}

const props = defineProps<{
  aiSummaryText: string | null
  aiTags: AiTag[]
  isProductPurchasedByUser: boolean | null
  productId: string
  userReviewId: string | null
}>()

const emit = defineEmits<{
  (e: 'setTotalPublishedReviews', x: number): void
  (e: 'setUserReviewId', x: string | null): void
}>()

const page = ref<Page>({ number: 1, size: 10 })
const totalPublishedReviews = ref<number | null>(null)
const totalPagesOfReviews = ref<number | null>(null)

const productReviewsKey = computed(() => `product-reviews-${props.productId}`)

const { data: reviewsData, status: reviewsStatus } = await useLazyAsyncData(productReviewsKey, loadReviewsData)

async function changePageOfReviews (x: number): Promise<void> {
  scrollTo('scroll-anchor', 84)

  page.value.number = x

  await refreshNuxtData('reviewsDataKey')
}

async function loadReviewsData (): Promise<ReviewData[]> {
  const params = helpers.getParams([
    { name: 'filter[productId]', value: props.productId, condition: true },
    { name: 'page[number]', value: page.value.number, condition: true },
    { name: 'page[size]', value: page.value.size, condition: true }
  ])

  try {
    const { data, meta } = await $apiHelper.reviews.getReviews(params)
    const { totalReviews } = meta

    emit('setTotalPublishedReviews', totalReviews)

    totalPublishedReviews.value = totalReviews
    totalPagesOfReviews.value = helpers.getTotalPages(totalReviews, page.value.size)

    return data
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToGetReviews)

    throw error
  }
}
</script>

<template>
  <div>
    <AiReviews :ai-summary-text="aiSummaryText" :ai-tags="aiTags" class="mb-5" />

    <div id="scroll-anchor" />

    <Preloader v-if="reviewsStatus === 'pending'" />
    <template v-else-if="reviewsStatus === 'success' && reviewsData">
      <template v-if="reviewsData.length > 0">
        <ReviewCard v-for="x in reviewsData" :key="x.id" class="mb-3" :review-data="x" />
      </template>
      <Notification v-else status="info">
        {{ messages.noReviews }}
      </Notification>
    </template>
    <template v-else>
      <Notification class="mb-4" status="error">
        {{ messages.failedToGetReviews }}
      </Notification>

      <a href="#" @click.prevent="refreshNuxtData('reviewsDataKey')">Повторить попытку</a>
    </template>

    <Pagination :current-page="page.number" :last-page="totalPagesOfReviews" @change-page="changePageOfReviews" />
  </div>
</template>
